<template>
  <div>
    <TooltipProvider>
      <component :is="layout">
        <router-view v-slot="{ Component }">
          <component :is="Component" />
        </router-view>
      </component>
      <!--Global Alerts-->
      <Modal
        data-testid="modal-global"
        class="modal-global"
        :show="modal.show"
        :submit-button-text="modal.submitButtonText"
        :submit-button-type="modal.submitButtonType"
        :cancel-button="modal.cancelButton"
        :backdrop="modal.backdrop"
        @close="
          modal.onClose?.();
          modal.show = false;
        "
        @submit="
          modal.onSubmit?.();
          modal.show = false;
        "
      >
        <template #header>
          <h2 v-if="modal.title">{{ modal.title }}</h2>
        </template>
        <template #body>
          <p><span v-dompurify-html="modal.message"></span></p>
        </template>
      </Modal>

      <Teleport to="body">
        <KaToaster
          v-if="!isDrRestoreRunning && !toastStore.areToastsHidden"
          :toasts="toasts"
          position="top-center"
          class="z-[2300]"
          @remove="removeToast"
        />
      </Teleport>

      <EditorModal v-bind="editorModal" @close="editorModal.show = false" />

      <DrRestoreModal
        :open="isDrRestoreInProgress || drRestoreNeedsConfirmation"
        :status="drRestoreStore.drRestoreStatus?.drRestoreStatusResponse?.data"
      />

      <NewSidepanel2
        data-testid="alerts-sidepanel"
        :show="sidepanelAlertMsgs.show"
        @close="sidepanelAlertMsgs.show = false"
        label="Alert Messages"
        labelTag="h2"
        backdrop
      >
        <template #body>
          <AlertMessageList @close="sidepanelAlertMsgs.show = false" />
        </template>
      </NewSidepanel2>

      <GuidedTour v-if="routeRequiresAuth" />

      <!-- TODO: this is the fast fix for fast scroll issue in Safari. Background becomes white even though <html> was explicitly set to dark background -->
      <div class="fixed inset-0 z-[-1] dark:bg-navy-950" />
    </TooltipProvider>
  </div>
</template>

<script setup lang="ts">
import { useTimeoutPoll } from '@vueuse/core';
import { TooltipProvider } from 'reka-ui';
import { computed, onUnmounted, reactive, ref, watch } from 'vue';
import { useRoute } from 'vue-router';

import { type EditorModalOptions, type ModalOptions, removeToast, toasts } from '@/alerts';
import AlertMessageList from '@/components/AlertMessageList.vue';
import DrRestoreModal from '@/components/DrRestoreModal.vue';
import EditorModal from '@/components/EditorModal.vue';
import GuidedTour from '@/components/GuidedTour.vue';
import Modal from '@/components/Modal.vue';
import NewSidepanel2 from '@/components/NewSidepanel2.vue';
import { useColorMode } from '@/composables/useSharedColorMode';
import EventBus from '@/event-bus';
import { KaToaster } from '@/kasten-components';
import * as log from '@/logger';
import { pollingLoop } from '@/polling';
import { defaultErrorHandler } from '@/services';
import { useAppInfoStore } from '@/stores/appInfo';
import { useDrRestoreStore } from '@/stores/disasterRecovery/drRestoreStore';
import { useMainStore } from '@/stores/main';
import { useMultiClusterStore } from '@/stores/multiCluster';
import { useToastStore } from '@/stores/toast';

const POLLING_INTERVAL_MS = 10000;

// Initialize shared color mode once — enables automatic light/dark mode switching
useColorMode();

const route = useRoute();
const appInfoStore = useAppInfoStore();
const mainStore = useMainStore();
const multiClusterStore = useMultiClusterStore();
const drRestoreStore = useDrRestoreStore();
const toastStore = useToastStore();

// `show` is always present (set on init and by every showModal() dispatch), so narrow it to
// non-optional to satisfy the Modal component's required boolean `show` prop. Held in a ref so
// each dispatch replaces the config wholesale — Object.assign-ing into a reactive would leave
// stale fields (e.g. cancelButton) from a previous modal lingering.
const modal = ref<ModalOptions & { show: boolean }>({ show: false });
const editorModal = ref<EditorModalOptions>({ show: false });
const sidepanelAlertMsgs = reactive({ show: false });

// useTimeoutPoll waits for each pollingLoop() to resolve before scheduling the next run, so
// requests never overlap. Must be created at setup level so its onScopeDispose cleanup registers.
const pollingControls = useTimeoutPoll(async () => {
  await pollingLoop();
}, POLLING_INTERVAL_MS);
// No DOM dependency — start polling during setup for faster first data fetch.
pollingControls.resume();

const layout = computed(() => {
  const l = route.meta.layout;
  if (l && 'component' in l) return l.component;
  return l ?? 'div';
});

const routeRequiresAuth = computed(() => !!route.meta?.requiresAuth);

const proxyQueryParam = computed(() => {
  const cl = route.query?.cl;
  return Array.isArray(cl) ? cl[0] : cl;
});

const isDrRestoreInProgress = computed(() => drRestoreStore.drRestoreStatus?.isRestoreModalShowing);
const drRestoreNeedsConfirmation = computed(
  () => drRestoreStore.drRestoreStatus?.isCompletedRestoreModalShowing === true,
);
const isDrRestoreRunning = computed(() => drRestoreStore.drRestoreStatus.isRestoreRunning);
const userInfo = computed(() => mainStore.userInfo);

function fetchAuthDependentData() {
  if (appInfoStore.systemInfo === null) {
    appInfoStore.fetchSystemInfo().catch(defaultErrorHandler);
  }
  mainStore.fetchUserPermissions(proxyQueryParam.value ?? undefined);
  mainStore.fetchLiveNamespaces();
}

watch(
  userInfo,
  () => {
    log.debug('userInfo updated');
  },
  { deep: true },
);
watch(routeRequiresAuth, (newVal) => {
  if (newVal) fetchAuthDependentData();
});
watch(proxyQueryParam, (newVal) => {
  multiClusterStore.proxiedCluster = newVal ?? undefined;
  if (routeRequiresAuth.value) fetchAuthDependentData();
});

// EventBus handlers stored as named references so they can be removed precisely on unmount.
// Each dispatch replaces the modal config wholesale to avoid stale fields from a prior modal.
function onGlobalModalShow(options: ModalOptions) {
  modal.value = { ...options, show: true };
}
function onGlobalEditorModalShow(options: EditorModalOptions) {
  editorModal.value = { ...options, show: true };
}
function onAlertMessagesShow() {
  sidepanelAlertMsgs.show = true;
}

EventBus.$on<ModalOptions>('global-modal-show', onGlobalModalShow);
EventBus.$on<EditorModalOptions>('global-editor-modal-show', onGlobalEditorModalShow);
EventBus.$on('alert-messages-show', onAlertMessagesShow);

onUnmounted(() => {
  pollingControls.pause();
  EventBus.$off<ModalOptions>('global-modal-show', onGlobalModalShow);
  EventBus.$off<EditorModalOptions>('global-editor-modal-show', onGlobalEditorModalShow);
  EventBus.$off('alert-messages-show', onAlertMessagesShow);
});
</script>
